import ShadowContainerVertical, {
  IShadowContainerVerticalRef,
} from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { IStoreLocatorDealer } from '@models/IStoreLocatorDealer';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import DealersListingCard from './DealersListingCard';

export interface IDealersListingRef {
  readonly scrollToDealer: (dealer: IStoreLocatorDealer) => void;
  readonly scrollToTop: () => void;
}

interface Props {
  readonly dealers: IStoreLocatorDealer[];
  readonly directionsLabel: string;
  readonly goToDealerLabel: string;
  readonly resultLabel: string;
  readonly resultsLabel: string;
  readonly selectedDealer?: IStoreLocatorDealer | null;
  readonly viewOnMapLabel: string;
  readonly onViewOnMapClick?: (currentDealer: IStoreLocatorDealer) => void;
}

const DealersListing = forwardRef<IDealersListingRef, Props>(
  (
    {
      dealers,
      directionsLabel,
      goToDealerLabel,
      resultLabel,
      resultsLabel,
      selectedDealer,
      viewOnMapLabel,
      onViewOnMapClick,
    },
    ref
  ) => {
    const dealersListSectionRef = useRef<HTMLDivElement>(null);
    const dealersListContainerRef = useRef<IShadowContainerVerticalRef>(null);

    const getDealerCardTestId = (index: number) =>
      `CT-30-Store-Locator-Dealer-${index}`;

    const handleScrollToDealer = (dealer: IStoreLocatorDealer) => {
      const dealerIndex = dealers.findIndex(
        (currentDealer) =>
          currentDealer.latitude === dealer.latitude &&
          currentDealer.longitude === dealer.longitude &&
          currentDealer.title === dealer.title
      );
      if (dealerIndex === -1) return;

      const dealerCardElement =
        dealersListSectionRef.current?.querySelector<HTMLLIElement>(
          `:scope [data-testid="${getDealerCardTestId(dealerIndex)}"]`
        );
      if (!dealerCardElement) return;

      const topScrollOffset =
        dealerIndex === 0 ? 0 : dealerCardElement.offsetTop - 15;
      window.requestAnimationFrame(() =>
        dealersListContainerRef.current?.scrollTo(topScrollOffset)
      );
    };

    const handleScrollToTop = () => {
      window.requestAnimationFrame(() =>
        dealersListContainerRef.current?.scrollTo(0, 'instant')
      );
    };

    useImperativeHandle(ref, () => ({
      scrollToDealer: handleScrollToDealer,
      scrollToTop: handleScrollToTop,
    }));

    return (
      <div
        className="w-full h-full lg:w-1/2 lg:flex lg:flex-col relative bg-grey-100 pt-5 pb-5 lg:pb-2.5 max-lg:rounded-b-[10px] lg:rounded-[10px] xl:pt-10 xl:pb-7.5"
        data-testid="CT-30-Store-Locator-Dealers-Listing"
        ref={dealersListSectionRef}
      >
        <div
          className="body-two text-black mb-5 lg:mb-2.5 xl:mb-7.5 px-5 xl:px-13"
          data-testid="CT-30-Store-Locator-Dealers-Listing-Count"
          dangerouslySetInnerHTML={{
            __html: (dealers.length === 1 ? resultLabel : resultsLabel).replace(
              '{0}',
              `${dealers.length}`
            ),
          }}
        />

        <ShadowContainerVertical
          ref={dealersListContainerRef}
          className="overflow-y-hidden"
          shadowColour="from-grey-100"
          shadowHeight="h-5"
          hideScrollbar={false}
          customScrollbarTheme="light"
        >
          <ul className="flex flex-col gap-y-5 lg:py-2.5 lg:gap-y-7.5 px-5 xl:px-13">
            {dealers.map((dealer, index) => (
              <DealersListingCard
                key={`${dealer.latitude}-${dealer.longitude}-${dealer.title}`}
                dealer={dealer}
                directionsLabel={directionsLabel}
                dataTestId={getDealerCardTestId(index)}
                goToDealerLabel={goToDealerLabel}
                isSelectedDealer={
                  dealer.latitude === selectedDealer?.latitude &&
                  dealer.longitude === selectedDealer.longitude &&
                  dealer.title === selectedDealer.title
                }
                viewOnMapLabel={viewOnMapLabel}
                onViewOnMapClick={onViewOnMapClick}
              />
            ))}
          </ul>
        </ShadowContainerVertical>
      </div>
    );
  }
);

export default DealersListing;
